import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { DateRange } from 'react-date-range';
import {
  getDateDetailsFromDateTimeObj,
  getMonthDifferenceBetweenTwoDates,
} from 'utils/functions/dateTime';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import {
  RangeDatePickerWrapper,
  RangeDatePickerSelectorWrapper,
  RangeDatePickerLabel,
} from './RangeDatePicker.style';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const RangeDatePicker = ({ selectedDateFilterObj, handleRangeDatePickerChange }) => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const datePickerWrapperRef = useRef(null);
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [selectedDate, setSelectedDate] = useState([
    {
      startDate: selectedDateFilterObj?.startDate || new Date(),
      endDate: selectedDateFilterObj?.endDate || new Date(),
      key: 'selection',
    },
  ]);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [selectedDateLabel, setSelectedDateLabel] = useState(
    selectedDateFilterObj.selectedDateLabel || 'Please select a date',
  );
  const [selectDateStep, setSelectDateStep] = useState({
    startDate: false,
    endDate: false,
  });
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        datePickerWrapperRef.current &&
        datePickerWrapperRef.current.contains(event.target) === false
      )
        setIsDatePickerOpen(false);
    };
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [datePickerWrapperRef]);
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleDatePickerLabelClick = () => {
    setIsDatePickerOpen(prevState => !prevState);
  };

  const handleDatePickerChange = date => {
    const { selection: dateObj } = date;

    if (selectDateStep.startDate === false && selectDateStep.endDate === false) {
      setSelectedDate([dateObj]);
      setSelectDateStep({
        startDate: true,
        endDate: true,
      });
    } else if (selectDateStep.startDate === true) {
      if (getMonthDifferenceBetweenTwoDates(dateObj.startDate, dateObj.endDate) > 1) {
        setSelectDateStep({
          startDate: false,
          endDate: false,
        });

        return;
      }

      setSelectedDate([dateObj]);
      setSelectDateStep({
        startDate: false,
        endDate: false,
      });

      setIsDatePickerOpen(false);

      const {
        day: startDay,
        month: startMonth,
        year: startYear,
      } = getDateDetailsFromDateTimeObj(dateObj.startDate);
      const {
        day: endDay,
        month: endMonth,
        year: endYear,
      } = getDateDetailsFromDateTimeObj(dateObj.endDate);
      setSelectedDateLabel(
        `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`,
      );

      handleRangeDatePickerChange(date.selection);
    }
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  return (
    <RangeDatePickerWrapper ref={datePickerWrapperRef}>
      <RangeDatePickerLabel
        data-testid="data-picker-label"
        onClick={() => handleDatePickerLabelClick()}
      >
        {selectedDateLabel}
      </RangeDatePickerLabel>
      {isDatePickerOpen && (
        <RangeDatePickerSelectorWrapper data-testid="data-picker-selector">
          <DateRange
            editableDateInputs
            onChange={date => handleDatePickerChange(date)}
            moveRangeOnFirstSelection={false}
            ranges={selectedDate}
          />
        </RangeDatePickerSelectorWrapper>
      )}
    </RangeDatePickerWrapper>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

RangeDatePicker.propTypes = {
  selectedDateFilterObj: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    selectedDateLabel: PropTypes.string,
  }),
  handleRangeDatePickerChange: PropTypes.func.isRequired,
};

RangeDatePicker.defaultProps = {
  selectedDateFilterObj: {},
};

export default RangeDatePicker;
